import { computed, ref, watch } from 'vue'

import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'

import { useModelQueries } from '@/apiClient/queries/useModelQueries'
import type { IModel } from '@/apiClient/types/model'
import { useMetricEvents } from '@/composables/useMetricEvents'
import { PosthogFeatureFlags, usePosthogFlag } from '@/lib/posthog'

import { defaultModels } from './defaultModels'
import {
  IMAGE_GENERATION_MODEL,
  isWebSearchModelSlug,
  PERPLEXITY_SLUGS,
} from './specialPurposeModels'

// Used for displaying 'Web Search' as a model in the 'Generated by' label of the responses
const PERPLEXITY_MODEL: IModel = {
  slug: 'perplexity',
  foundry_id: 'perplexity',
  object: 'model',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  max_model_length: 999999,
  max_output_tokens: null,
  max_audio_size: null,
  display_name: 'Web Search',
  description: 'Web Search',
  description_long: 'Web Search',
  provider: 'perplexity',
  features: {
    instruct: true,
    vision: true,
  },
}

/** Matches `PERPLEXITY_MODEL.display_name` (web search assistant in chat). */
export const WEB_SEARCH_DISPLAY_NAME = PERPLEXITY_MODEL.display_name

export const IMAGE_GENERATION_DISPLAY_NAME = 'Image Generation'

export { IMAGE_GENERATION_MODEL } from './specialPurposeModels'

/**
 * Readable stand-in for a model the API no longer lists: its own slug, minus the
 * `@foundry/region/` routing prefix that means nothing to a reader. Deactivating or
 * hiding a model in admin drops it from `GET /llms` entirely, and chats it already
 * answered must keep saying so rather than losing their attribution.
 */
const retiredModelLabel = (slug: string) => slug.split('/').pop()?.trim() || ''

/**
 * Dated snapshot a provider appends to the deployment it served
 * (`gpt-4o-mini-2024-07-18`, `claude-3-5-sonnet-20241022`).
 */
const SNAPSHOT_SUFFIX = /-\d{4}-?\d{2}-?\d{2}$/

/**
 * Whether two model labels name genuinely different chat models — the test behind
 * both the manual model-switch notice and the backend-fallback note. Web search and
 * image generation answer on their own models by design, so a difference involving
 * either is a change of mode, not of model, and must not be reported as one.
 */
export const isDifferentChatModel = (first: string, second: string) =>
  !!first &&
  !!second &&
  first !== second &&
  ![WEB_SEARCH_DISPLAY_NAME, IMAGE_GENERATION_DISPLAY_NAME].some((name) =>
    [first, second].includes(name),
  )

/** Preselected until the user picks something (GPT-5.6 Luna). */
const DEFAULT_MODEL_SLUG = '@zo/azure-sweden/gpt-5.6-luna'

/**
 * Storage keys for the global model pick. Bump the suffix to reset every user's
 * pick back to the default — the old keys are simply never read.
 */
const SEED_MODEL_STORAGE_KEY = 'selected_model_id_v3'
const TAB_MODEL_STORAGE_KEY = 'tab_selected_model_id_v3'

/** Cap on remembered per-chat picks. The least recently set are dropped first. */
const MAX_REMEMBERED_CHAT_MODELS = 200

const perplexitySeed = () =>
  PERPLEXITY_SLUGS.reduce(
    (accum, slug) => {
      accum[slug] = PERPLEXITY_MODEL
      return accum
    },
    {} as Record<string, IModel>,
  )

export const useModelStore = defineStore('model', () => {
  const { list } = useModelQueries()
  const { data, isFetching } = list()

  /**
   * Last-selected model memory (cross-tab seed + tab pick). Fail-open keeps
   * remembering when PostHog is not configured (dev / staging) or unreachable;
   * when the flag is off, neither storage key is read or written.
   */
  const isRememberLastSelectedModelOn = usePosthogFlag(
    PosthogFeatureFlags.RememberLastSelectedModel,
  )

  /**
   * 'Unpaginated' model list, filtered by whitelist
   */
  const models = computed(() => data.value?.items ?? [])

  /** Models the user may pick in chat / forms (excludes admin-hidden). */
  const selectableModels = computed(() =>
    models.value.filter((model) => !model.is_hidden),
  )
  /** Slugs the chat selector actually offers, so a seeded pick can't be one it hides. */
  const pickableModelSlugs = computed(
    () => new Set(selectableModels.value.map((model) => model.slug)),
  )

  /**
   * First model the chat selector lists. The API already returns models in admin
   * `position` order and the selector leads with the featured ones, so that is
   * the first featured model, if any.
   */
  const firstOfferedModel = computed(
    () =>
      selectableModels.value.find((model) => model.is_featured) ??
      selectableModels.value[0],
  )

  /**
   * Most recent pick from any tab. Only seeds a tab that has none of its own, so a
   * newly opened one starts on the last model used rather than the default.
   * Hydrated from / written to localStorage only while the remember flag is on.
   */
  const persistedSeedModelSlug = ref(DEFAULT_MODEL_SLUG)

  const readPersistedSeedModelSlug = () => {
    try {
      return localStorage.getItem(SEED_MODEL_STORAGE_KEY) ?? DEFAULT_MODEL_SLUG
    } catch {
      return DEFAULT_MODEL_SLUG
    }
  }

  const writePersistedSeedModelSlug = (slug: string) => {
    try {
      localStorage.setItem(SEED_MODEL_STORAGE_KEY, slug)
    } catch {
      // Private mode / quota — in-memory seed still moves this tab's new chats.
    }
  }

  const readTabModelSlug = (): string | null => {
    try {
      return sessionStorage.getItem(TAB_MODEL_STORAGE_KEY)
    } catch {
      return null
    }
  }

  const writeTabModelSlug = (slug: string) => {
    try {
      sessionStorage.setItem(TAB_MODEL_STORAGE_KEY, slug)
    } catch {
      // Private mode / quota — in-memory pick still moves this tab's new chats.
    }
  }

  /**
   * Last model picked *in this tab*. Preselected in every chat with no pick of its
   * own, so a new chat opens on whatever was used last here. sessionStorage only
   * while the remember flag is on — survives a reload, never crosses tabs. With
   * the flag off it is reset to the default on every scope change.
   */
  const lastSelectedModelSlug = ref(DEFAULT_MODEL_SLUG)

  // Hydrate (or reset) when the flag settles. Fail-open starts on, so a clean
  // PostHog delivery that turns the flag off drops any value we had read.
  watch(
    isRememberLastSelectedModelOn,
    (isOn) => {
      if (isOn) {
        persistedSeedModelSlug.value = readPersistedSeedModelSlug()
        lastSelectedModelSlug.value =
          readTabModelSlug() ?? persistedSeedModelSlug.value
      } else {
        persistedSeedModelSlug.value = DEFAULT_MODEL_SLUG
        lastSelectedModelSlug.value = DEFAULT_MODEL_SLUG
      }
    },
    { immediate: true },
  )

  /**
   * Model picked per chat, keyed by chat id. Persisted so a chat reopens on the
   * model it was last set to, and capped so it cannot grow without bound.
   */
  const chatModelSlugs = useLocalStorage<Record<string, string>>(
    'chat_model_ids',
    {},
  )

  /** Chat the selector currently targets. Empty on new-chat surfaces. */
  const activeChatId = ref('')
  /** Scope the selector to a chat; pass `''` when leaving one. */
  const setActiveChatScope = (chatId: string) => {
    // Remembering off: a pick ends with the scope it was made on, as a reload would.
    if (!isRememberLastSelectedModelOn.value && chatId !== activeChatId.value) {
      lastSelectedModelSlug.value = DEFAULT_MODEL_SLUG
    }
    activeChatId.value = chatId
  }

  const activeModelSlug = computed(
    () =>
      chatModelSlugs.value[activeChatId.value] ?? lastSelectedModelSlug.value,
  )
  /**
   * Falls back to the first offered model when the remembered (or default) slug
   * resolves to nothing — a retired or newly hidden model, one this environment
   * does not list, or a list that has not loaded yet. Derived rather than
   * written back, so the remembered pick returns if the model does, and so an
   * automatic repair can never stamp itself onto the open chat ahead of
   * `seedChatModel`.
   */
  const activeModel = computed(
    () =>
      modelsIdMap.value[activeModelSlug.value] ??
      firstOfferedModel.value ??
      models.value[0],
  )
  /** Lookup by model slug (same value as API `model` / GPT `config.model`). */
  const modelsIdMap = computed<Record<string, IModel>>(() =>
    [...defaultModels, ...models.value].reduce((accum, model) => {
      accum[model.slug] = model
      return accum
    }, perplexitySeed()),
  )

  /**
   * Resolve a model from a message/API model string (exact slug, or the slug
   * minus a dated snapshot suffix).
   */
  const modelsBaseIdMap = computed<Record<string, IModel>>(() =>
    [...defaultModels, ...models.value].reduce((accum, model) => {
      accum[model.slug] = model
      return accum
    }, perplexitySeed()),
  )
  const getModelByBaseId = (baseId: string) => {
    // baseId is a base_model with a date appended. It should drop the date and match (fully) the base_model name
    // i.e. gpt-4o-mini-2024-07-18 should map to 'gpt-4o-mini' and return modelsBaseIdMap['gpt-4o-mini']
    const map = modelsBaseIdMap.value
    // A message may carry a foundry-prefixed slug (`@px/perplexity/sonar`) while the
    // known model is keyed without the prefix (or the other way round), so try the
    // slug as-is first and then with its `@foundry/` segment stripped.
    const candidates = [baseId, baseId.replace(/^@[^/]+\//, '')]
    for (const candidate of candidates) {
      // Only a dated snapshot suffix is stripped, never an arbitrary tail. One slug
      // can prefix another (`…/gpt-5.2` vs `…/gpt-5.2-chat`), so a prefix match
      // hands a deactivated model's replies its still-listed sibling's name — and
      // seeds the chat onto that sibling — instead of falling back to the slug.
      const match =
        map[candidate] ?? map[candidate.replace(SNAPSHOT_SUFFIX, '')]
      if (match) return match
    }
    // Web search is seeded under its base slug only, but answers on variants of it.
    if (isWebSearchModelSlug(baseId)) return PERPLEXITY_MODEL
    return undefined
  }

  /**
   * Label for a model slug carried on a message. Image generation is not a listed
   * model, so it is named by hand, and a slug the catalogue no longer knows falls
   * back to its own tail — only an absent slug resolves to nothing.
   */
  const getModelDisplayName = (slug: string) => {
    if (!slug) return ''
    if (slug === IMAGE_GENERATION_MODEL) return IMAGE_GENERATION_DISPLAY_NAME
    return getModelByBaseId(slug)?.display_name || retiredModelLabel(slug)
  }

  const doesModelSupportFileUpload = computed(
    () => activeModel.value?.features.vision,
  )

  /**
   * Pin a chat to a model. Called for every pick made while a chat is in scope,
   * and for the model a chat is created/sent on — that one is picked on the
   * new-chat surface, where there is no chat id to attach it to yet, so without
   * pinning it the chat owns no model and silently follows
   * `lastSelectedModelSlug`, which any other tab can move.
   */
  const rememberChatModel = (chatId: string, slug: string) => {
    if (!chatId || !slug) return
    const picks = { ...chatModelSlugs.value }
    // Re-insert rather than overwrite, so the newest pick sorts last and is the
    // last one the cap below would ever drop.
    delete picks[chatId]
    picks[chatId] = slug

    const chatIds = Object.keys(picks)
    chatIds
      .slice(0, Math.max(0, chatIds.length - MAX_REMEMBERED_CHAT_MODELS))
      .forEach((staleChatId) => delete picks[staleChatId])

    chatModelSlugs.value = picks
  }

  /**
   * Adopt the model a chat last ran on, so reopening it (or opening it on
   * another device) shows that model instead of the global default. Never
   * overrides a pick the user already made in that chat.
   *
   * Takes the slugs its messages name, newest first, and seeds from the first
   * one the selector actually offers. Image generation and web search answer on
   * models of their own (`gpt-image-1`, perplexity) that are never listed, so a
   * chat whose newest reply is one of those must be seeded from the chat model
   * underneath it — stopping at the newest slug would leave it unseeded and
   * therefore adrift on `lastSelectedModelSlug`.
   */
  const seedChatModel = (chatId: string, ...messageModelSlugs: string[]) => {
    if (!chatId || chatModelSlugs.value[chatId]) return
    for (const slug of messageModelSlugs) {
      const model = slug ? getModelByBaseId(slug) : undefined
      // Unresolved while the model list is still loading, and unpickable for the
      // modes above — neither is a pick worth remembering.
      if (!model || !pickableModelSlugs.value.has(model.slug)) continue
      rememberChatModel(chatId, model.slug)
      return
    }
  }

  const setActiveModel = (model: IModel) => {
    const previous = activeModel.value?.slug ?? activeModelSlug.value
    // In-memory so the selector moves now; flag off keeps it only until the scope changes.
    lastSelectedModelSlug.value = model.slug
    if (isRememberLastSelectedModelOn.value) {
      writeTabModelSlug(model.slug)
      // Seed only: it moves the next tab to open, never one already open.
      persistedSeedModelSlug.value = model.slug
      writePersistedSeedModelSlug(model.slug)
    }
    if (activeChatId.value) rememberChatModel(activeChatId.value, model.slug)
    useMetricEvents().sendEvent('model_switch', {
      model: {
        previous,
        current: model.slug,
      },
    })
  }

  return {
    isFetching,
    models,
    selectableModels,
    modelsIdMap,
    activeModel,
    doesModelSupportFileUpload,
    setActiveModel,
    setActiveChatScope,
    rememberChatModel,
    seedChatModel,
    getModelByBaseId,
    getModelDisplayName,
  }
})
